import { useEffect, useState } from 'react'
import { View, Text, ActivityIndicator } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle } from '@/components/ui/icons'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, withAlpha, SPACING } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { supabase } from '@/lib/supabase'
import { captureError } from '@/lib/utils/observability'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'
import { parsePaymentReturnParams } from '@/lib/payments/return-params'
import { postThreeDsCompleteToOpener } from '@/lib/payments/three-ds-complete'

// 'unconfirmed' is the honest end of a bounded poll: the provider has not said yes or
// no. It used to fall through to 'failed', which told a guest their payment was
// rejected while it was still being approved — and the retry button then aimed
// them at a second charge. 'refunded' is the charge that landed on a booking
// that was already gone.
type PaymentOutcome = 'loading' | 'success' | 'failed' | 'unconfirmed' | 'refunded' | 'cancelled'
type PaymentConfirmation = {
  status: 'captured' | 'failed' | 'refunded'
  rentalId: string | null
}

async function confirmPaymentFromProvider(params: {
  transactionId?: string
  rentalId?: string
}): Promise<PaymentConfirmation | null> {
  if (!params.transactionId) return null

  const { data, error } = await supabase.functions.invoke('confirm-payment', {
    body: {
      transactionId: params.transactionId,
      ...(params.rentalId ? { rentalId: params.rentalId } : {}),
    },
  })

  if (error) return null
  if (data?.status === 'captured') {
    return { status: 'captured', rentalId: (data.rentalId as string | undefined) ?? null }
  }
  if (data?.status === 'failed') {
    return { status: 'failed', rentalId: (data.rentalId as string | undefined) ?? null }
  }
  // Charged against a rental that had stopped waiting for it; the server has
  // already reversed it. Terminal, and emphatically not a success.
  if (data?.status === 'refund_required') {
    return { status: 'refunded', rentalId: (data.rentalId as string | undefined) ?? null }
  }
  return null
}

export default function PaymentCompleteScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const params = useLocalSearchParams()
  const returned = parsePaymentReturnParams(params as Record<string, unknown>)
  const [outcome, setOutcome] = useState<PaymentOutcome>('loading')
  const [confirmedRentalId, setConfirmedRentalId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function verify() {
      const provider = returned.provider
      const transactionId = returned.transactionId
      const rentalIdFromReturn = returned.rentalId
      postThreeDsCompleteToOpener({
        transactionId: transactionId ?? undefined,
        rentalId: rentalIdFromReturn ?? undefined,
        provider: provider ?? undefined,
      })
      productTelemetryService.recordEvent('payment_step', 'payment.callback_opened', {
        elementId: 'payment-complete.screen',
        metadata: { hasTransactionId: Boolean(transactionId), provider },
      })

      if (returned.cancelled) {
        productTelemetryService.recordEvent('payment_step', 'payment.callback_cancelled', {
          elementId: 'payment-complete.screen',
          metadata: { provider },
        })
        setConfirmedRentalId(rentalIdFromReturn)
        setOutcome('cancelled')
        return
      }

      if (!transactionId) {
        productTelemetryService.recordEvent('payment_step', 'payment.callback_failed', {
          elementId: 'payment-complete.screen',
          metadata: { reason: 'missing_transaction_id' },
        })
        setOutcome('failed')
        return
      }

      for (let attempt = 0; attempt < 10; attempt++) {
        if (cancelled) return
        if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 2000))
        if (cancelled) return

        const query = supabase
          .from('payments')
          .select('status,rental_id')
          .order('created_at', { ascending: false })
          .limit(1)
          .eq('provider_payment_id', transactionId)
        const scoped = provider ? query.eq('provider', provider) : query

        const { data } = await scoped.maybeSingle()
        if (data?.status === 'captured') {
          const rentalId = (data.rental_id as string | undefined) ?? null
          setConfirmedRentalId(rentalId)

          // A captured payment is only a booking if the RENTAL confirmed. The
          // dead-rental path deliberately leaves the payment 'captured' so
          // refund-payment can still see it, so trusting the payment row alone
          // is what turned a reversed charge into a success screen.
          const { data: rental } = rentalId
            ? await supabase.from('rentals').select('status').eq('id', rentalId).maybeSingle()
            : { data: null }

          if (!rental) {
            setOutcome('unconfirmed')
            return
          }
          if (rental.status !== 'confirmed' && rental.status !== 'awaiting_host') {
            productTelemetryService.recordEvent('payment_step', 'payment.refunded', {
              elementId: 'payment-complete.screen',
              metadata: { source: 'payment_row', rentalStatus: rental.status },
            })
            setOutcome('refunded')
            return
          }

          productTelemetryService.recordEvent('payment_step', 'payment.callback_captured', {
            elementId: 'payment-complete.screen',
            metadata: { source: 'payment_row' },
          })
          setOutcome('success')
          return
        }
        if (data?.status === 'failed') {
          productTelemetryService.recordEvent('payment_step', 'payment.callback_failed', {
            elementId: 'payment-complete.screen',
            metadata: { source: 'payment_row' },
          })
          setOutcome('failed')
          return
        }

        const providerStatus = await confirmPaymentFromProvider({
          transactionId: transactionId ?? undefined,
          rentalId: rentalIdFromReturn ?? undefined,
        })
        if (providerStatus?.status === 'captured') {
          setConfirmedRentalId(providerStatus.rentalId)
          productTelemetryService.recordEvent('payment_step', 'payment.callback_captured', {
            elementId: 'payment-complete.screen',
            metadata: { source: 'provider_confirm' },
          })
          setOutcome('success')
          return
        }
        if (providerStatus?.status === 'refunded') {
          setConfirmedRentalId(providerStatus.rentalId)
          productTelemetryService.recordEvent('payment_step', 'payment.refunded', {
            elementId: 'payment-complete.screen',
            metadata: { source: 'provider_confirm' },
          })
          setOutcome('refunded')
          return
        }
        if (providerStatus?.status === 'failed') {
          setConfirmedRentalId(providerStatus.rentalId)
          productTelemetryService.recordEvent('payment_step', 'payment.callback_failed', {
            elementId: 'payment-complete.screen',
            metadata: { source: 'provider_confirm' },
          })
          setOutcome('failed')
          return
        }
      }

      // The poll ran out. That is not a decline — reporting one here is what
      // sent guests back to checkout to pay a second time for a charge that was
      // still in flight.
      productTelemetryService.recordEvent('payment_step', 'payment.unconfirmed', {
        elementId: 'payment-complete.screen',
        metadata: { source: 'provider_timeout' },
      })
      setOutcome('unconfirmed')
    }

    // The verify loop polls the selected provider through the edge function for a bounded
    // 10 attempts. Any error becomes a failed state instead of an indefinite
    // spinner, so the user can retry from checkout.
    verify().catch((err) => {
      if (cancelled) return
      captureError(err, { screen: 'payment-complete', action: 'verify' })
      productTelemetryService.recordEvent('payment_step', 'payment.callback_failed', {
        elementId: 'payment-complete.screen',
        metadata: { source: 'client_exception' },
      })
      setOutcome('failed')
    })
    return () => {
      cancelled = true
    }
  }, [returned.cancelled, returned.provider, returned.rentalId, returned.transactionId])

  const isLoading = outcome === 'loading'
  const isSuccess = outcome === 'success'
  // Neither a booking nor a decline. Retrying is the one thing a guest must not
  // do from here: the charge may still land, or it already landed and came back.
  const isUnresolved = outcome === 'unconfirmed' || outcome === 'refunded'

  const headline = isSuccess
    ? t('payment.success')
    : outcome === 'refunded'
      ? t('payment.refunded_title')
      : outcome === 'unconfirmed'
        ? t('payment.unconfirmed_title')
        : outcome === 'cancelled'
          ? t('payment.cancelled_title')
          : t('payment.failed')

  const detail = isSuccess
    ? t('payment.success_desc')
    : outcome === 'refunded'
      ? t('payment.refunded_desc')
      : outcome === 'unconfirmed'
        ? t('payment.unconfirmed_desc')
        : outcome === 'cancelled'
          ? t('payment.cancelled_desc')
          : t('payment.failed_desc')

  return (
    <ScreenContainer scroll={false}>
      {/* Every sibling of this screen (cancel, review, checkout, insurance)
          carries the standard header. Without it this one opened with a third
          of the display as bare background and no way back — on a decline the
          only affordance was "Reintentar pago". */}
      <ScreenHeader
        title={t('payment.title')}
        fallbackHref="/(tabs)/trips"
        backTestID="payment-complete.back"
      />
      <View
        style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: SPACING.xl }}
      >
        {isLoading ? (
          <>
            <ActivityIndicator size="large" color={COLORS.coral.DEFAULT} />
            <Text
              style={{
                fontSize: 15,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
                textAlign: 'center',
                marginTop: SPACING.md,
              }}
            >
              {t(
                params.provider === 'paypal' ? 'payment.redirecting_paypal' : 'payment.redirecting',
              )}
            </Text>
          </>
        ) : (
          <>
            <View
              style={{
                width: 80,
                height: 80,
                borderRadius: 40,
                // 8% over a near-black surface left the disc invisible in dark
                // mode, so the glyph read as a bare outline floating on the
                // background. The tint is raised for dark only.
                backgroundColor: withAlpha(
                  isSuccess ? colors.success : isUnresolved ? colors.warning : colors.error,
                  isDark ? 0.22 : 0.1,
                ),
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: SPACING.lg,
              }}
            >
              {isSuccess ? (
                <CheckCircle size={40} color={colors.success} strokeWidth={1.5} />
              ) : (
                <AlertCircle
                  size={40}
                  color={isUnresolved ? colors.warning : colors.textError}
                  strokeWidth={1.5}
                />
              )}
            </View>

            <Text
              style={{
                fontSize: 20,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
                textAlign: 'center',
              }}
            >
              {headline}
            </Text>

            <Text
              style={{
                fontSize: 15,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
                textAlign: 'center',
                marginTop: SPACING.sm,
                maxWidth: 360,
              }}
            >
              {detail}
            </Text>

            <View style={{ marginTop: SPACING.xl, width: '100%' }}>
              <Button
                title={
                  isSuccess
                    ? t('common.continue')
                    : isUnresolved
                      ? t('payment.unconfirmed_action')
                      : t('payment.retry')
                }
                onPress={() => {
                  if (isSuccess) {
                    router.replace(
                      confirmedRentalId
                        ? (`/booking/confirmation?rentalId=${confirmedRentalId}` as never)
                        : '/booking/confirmation',
                    )
                    return
                  }
                  if (isUnresolved) {
                    router.replace('/(tabs)/trips')
                    return
                  }
                  if (outcome === 'cancelled') {
                    router.replace(
                      confirmedRentalId
                        ? (`/booking/checkout?rentalId=${confirmedRentalId}` as never)
                        : '/booking/checkout',
                    )
                    return
                  }
                  router.replace(
                    confirmedRentalId
                      ? (`/booking/checkout?rentalId=${confirmedRentalId}` as never)
                      : '/booking/checkout',
                  )
                }}
                variant="primary"
                size="lg"
                testID={
                  isSuccess
                    ? 'payment-complete.continue'
                    : isUnresolved
                      ? 'payment-complete.go-to-trips-primary'
                      : outcome === 'cancelled'
                        ? 'payment-complete.retry-paypal'
                        : 'payment-complete.retry'
                }
              />

              {/* A declined payment is not a dead end: retrying is one option,
                  going back to the trips list is the other. */}
              {isSuccess || isUnresolved || outcome === 'cancelled' ? null : (
                <View style={{ marginTop: SPACING.sm }}>
                  <Button
                    title={t('payment.unconfirmed_action')}
                    onPress={() => router.replace('/(tabs)/trips')}
                    variant="text"
                    size="md"
                    testID="payment-complete.go-to-trips"
                  />
                </View>
              )}
            </View>
          </>
        )}
      </View>
    </ScreenContainer>
  )
}
